package com.arena_web.ui.components.dropdown

import androidx.compose.runtime.Composable
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.m
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mFontSize
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mLetterSpacing
import com.arena_web.ui.utils.mLineHeight
import com.arena_web.ui.utils.mPadding
import com.arena_web.ui.utils.mPaddingLeft
import com.arena_web.ui.utils.mPaddingRight
import com.arena_web.ui.utils.mSize
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

/**
 * City-picker dropdown panel — the Location counterpart of [SportDropdownPanel].
 *
 * Visually identical to the sport panel: a #09142d surface whose rows each carry
 * a 36-px round chip, a label, and a brand-blue check on the active row. The
 * sport panel paints a sport glyph inside the chip; here the chip holds the
 * city's flag emoji, and the synthetic "All cities" row gets the same globe icon
 * the sport panel uses for its "All" entry.
 *
 * Shared by the desktop action-bar Location pill and the mobile location pill,
 * so both surfaces stay in lockstep with the sport dropdown. Sizes use the `m()`
 * helpers (1 px above the 390-px mobile baseline) so the panel renders at design
 * size on desktop and scales down on narrower phones.
 *
 * Caller owns positioning (wrap in an absolutely-positioned container).
 *
 * `selectedCity == null` selects the "All cities" row; `onSelect(null)` is the
 * "clear filter" callback.
 */
@Composable
fun LocationDropdownPanel(
    selectedCity: String?,
    cities: List<String>,
    cityFlags: Map<String, String>,
    onSelect: (String?) -> Unit,
    modifier: Modifier = Modifier,
    // See SportDropdownPanel.scale — overrides `--mobile-scale` so every internal
    // m() value scales together. 1.0 = design size.
    scale: Double = 1.0,
) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .mBorderRadius(26)
            .mPadding(v = 12, h = 0)
            .styleModifier {
                property("background", "#09142d")
                property("min-width", m(200))
                if (scale != 1.0) property("--mobile-scale", "${scale}px")
            }
            .then(modifier)
    ) {
        Column(modifier = Modifier.fillMaxWidth().mGap(8)) {
            LocationDropdownPanelRow(
                label = t.dropdownAllCities,
                flag = "",
                isAll = true,
                isSelected = selectedCity == null,
                onSelect = { onSelect(null) },
            )
            cities.forEach { city ->
                LocationDropdownPanelRow(
                    label = city,
                    flag = cityFlags[city].orEmpty(),
                    isAll = false,
                    isSelected = city == selectedCity,
                    onSelect = { onSelect(city) },
                )
            }
        }
    }
}

@Composable
private fun LocationDropdownPanelRow(
    label: String,
    flag: String,
    isAll: Boolean,
    isSelected: Boolean,
    onSelect: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .mPaddingLeft(12)
            .mPaddingRight(16)
            .mGap(8)
            .cursor(Cursor.Pointer)
            .onClick { onSelect() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        LocationDropdownPanelIcon(flag = flag, isAll = isAll, isSelected = isSelected)
        SpanText(
            label.replaceFirstChar { it.uppercase() },
            modifier = Modifier
                .flexGrow(1)
                .mFontSize(16)
                .mLineHeight(24)
                .mLetterSpacing(0.16)
                .fontWeight(400)
                .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
                .color(if (isSelected) AppColors.PrimaryWhite else AppColors.Text.Secondary)
        )
        if (isSelected) {
            // Mask-image (not <img>) so the SVG recolours to brand blue — same
            // treatment as SportDropdownPanel's check.
            Box(
                modifier = Modifier
                    .mSize(20)
                    .styleModifier {
                        property("background-color", AppColors.Primary.toString())
                        property("-webkit-mask-image", "url(\"${AppIcons.Check}\")")
                        property("mask-image", "url(\"${AppIcons.Check}\")")
                        property("-webkit-mask-size", "contain")
                        property("mask-size", "contain")
                        property("-webkit-mask-repeat", "no-repeat")
                        property("mask-repeat", "no-repeat")
                        property("-webkit-mask-position", "center")
                        property("mask-position", "center")
                    }
            )
        }
    }
}

@Composable
private fun LocationDropdownPanelIcon(
    flag: String,
    isAll: Boolean,
    isSelected: Boolean,
) {
    val colors = LocalSportColors.current
    Box(
        modifier = Modifier
            .mSize(32)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            },
        contentAlignment = Alignment.Center
    ) {
        if (isAll) {
            // "All cities" → globe, matching the sport panel's "All" row.
            val iconColor = if (isSelected) colors.primary.toString() else "#B4CCF6"
            Box(
                modifier = Modifier
                    .mSize(16)
                    .styleModifier {
                        property("background-color", iconColor)
                        property("-webkit-mask-image", "url(\"${AppIcons.Globe}\")")
                        property("mask-image", "url(\"${AppIcons.Globe}\")")
                        property("-webkit-mask-size", "contain")
                        property("mask-size", "contain")
                        property("-webkit-mask-repeat", "no-repeat")
                        property("mask-repeat", "no-repeat")
                        property("-webkit-mask-position", "center")
                        property("mask-position", "center")
                    }
            )
        } else {
            // Flag emoji renders in its own colours on either chip background.
            SpanText(
                flag,
                modifier = Modifier
                    .mFontSize(20)
                    .styleModifier {
                        property("line-height", "1")
                        property("white-space", "nowrap")
                    }
            )
        }
    }
}
